import { useState, useEffect } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';

/**
 * Text-based numeric input that renders no native number spinner arrows
 * (works consistently across browsers, unlike `type="number"`).
 *
 * UX:
 *  - A zero (or empty) value is displayed as "0.00" ("0" when `decimals={0}`).
 *  - On focus the value clears so the user can type a fresh number
 *    (non-zero values are selected so typing replaces them).
 *  - On blur, an empty field is restored to zero and shown as "0.00" ("0" when
 *    `decimals={0}`).
 *
 * Accepts only digits and an optional decimal point (up to `decimals` places).
 * Values are clamped to a minimum of 0 and, when `max` is provided, to that
 * maximum.
 *
 * `value` is intentionally tolerant of the decimal *strings* that the API
 * returns for money/decimal columns (e.g. "0.00", "500.00") as well as real
 * numbers and empty/undefined. `onChange` always emits a `number`.
 */

const zeroFor = (decimals: number) => (decimals > 0 ? `0.${'0'.repeat(decimals)}` : '0');

/** Normalises any incoming value to the text shown in the input. */
function toDisplay(value: number | string | null | undefined, decimals: number): string {
  if (value === null || value === undefined || value === '') return zeroFor(decimals);
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return zeroFor(decimals);
  return String(num);
}

export function ValidatedNumberInput({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  className,
  max,
  decimals = 2,
  placeholder,
  title,
}: {
  /** Optional id so a `<label htmlFor>` can be attached. */
  id?: string;
  value: number | string | null | undefined;
  onChange: (value: number) => void;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  max?: number;
  /** Number of decimal places accepted. Use 0 for whole-number fields. */
  decimals?: number;
  placeholder?: string;
  title?: string;
}) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState<string>(() => toDisplay(value, decimals));

  // Re-sync when the value changes externally (e.g. switching receipts/tests).
  // Skip while the field is focused so typing isn't overwritten.
  useEffect(() => {
    if (focused) return;
    setText(toDisplay(value, decimals));
  }, [value, focused, decimals]);

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    if (Number(text) === 0 || text.trim() === '') {
      setText('');
    } else {
      e.target.select();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow empty, digits and (when decimals > 0) an optional decimal part.
    const pattern = decimals > 0 ? new RegExp(`^\\d*\\.?\\d{0,${decimals}}$`) : /^\d*$/;
    if (raw !== '' && !pattern.test(raw)) return;
    setText(raw);
    if (raw === '' || raw === '.') {
      onChange(0);
      return;
    }
    const num = Number(raw);
    if (!Number.isNaN(num)) onChange(num);
  };

  const handleBlur = () => {
    setFocused(false);
    let num = Number(text);
    if (text.trim() === '' || Number.isNaN(num)) num = 0;
    num = Math.max(0, num);
    if (max != null) num = Math.min(max, num);
    setText(num ? String(num) : zeroFor(decimals));
    onChange(num);
    onBlur?.();
  };

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      value={text}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      title={title}
      className={className}
    />
  );
}
